import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { mockPhotos } from "@/mocks/photos";

interface PhotoSlide {
  id: string;
  image_url: string;
  alt_text: string;
  caption_en: string;
  caption_ps: string;
  caption_da: string;
  sort_order: number;
  is_active: boolean;
}

const emptyPhoto: PhotoSlide = {
  id: "",
  image_url: "",
  alt_text: "",
  caption_en: "",
  caption_ps: "",
  caption_da: "",
  sort_order: 0,
  is_active: true,
};

export default function AdminPhotosPage() {
  const [photos, setPhotos] = useState<PhotoSlide[]>([]);
  const [editing, setEditing] = useState<PhotoSlide | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState<PhotoSlide>(emptyPhoto);
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    setPhotos([...mockPhotos].sort((a, b) => a.sort_order - b.sort_order));
  }, []);

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditing(null);
    setForm({ ...emptyPhoto, sort_order: photos.length });
  };

  const handleEdit = (photo: PhotoSlide) => {
    setEditing(photo);
    setIsAdding(false);
    setForm({ ...photo });
  };

  const handleDelete = (id: string) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
    showSuccess("Photo deleted successfully.");
  };

  const handleToggleActive = (id: string) => {
    setPhotos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, is_active: !p.is_active } : p))
    );
  };

  const handleSave = () => {
    if (!form.image_url.trim() || !form.caption_en.trim()) return;

    if (isAdding) {
      const newPhoto: PhotoSlide = {
        ...form,
        id: Date.now().toString(),
      };
      setPhotos((prev) => [...prev, newPhoto].sort((a, b) => a.sort_order - b.sort_order));
      showSuccess("Photo added successfully!");
    } else if (editing) {
      setPhotos((prev) =>
        prev
          .map((p) => (p.id === editing.id ? { ...form } : p))
          .sort((a, b) => a.sort_order - b.sort_order)
      );
      showSuccess("Photo updated successfully!");
    }

    setIsAdding(false);
    setEditing(null);
    setForm(emptyPhoto);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditing(null);
    setForm(emptyPhoto);
  };

  const movePhoto = (id: string, direction: "up" | "down") => {
    setPhotos((prev) => {
      const sorted = [...prev].sort((a, b) => a.sort_order - b.sort_order);
      const index = sorted.findIndex((p) => p.id === id);
      if (index === -1) return prev;
      if (direction === "up" && index === 0) return prev;
      if (direction === "down" && index === sorted.length - 1) return prev;

      const swapIndex = direction === "up" ? index - 1 : index + 1;
      const newSorted = [...sorted];
      [newSorted[index], newSorted[swapIndex]] = [newSorted[swapIndex], newSorted[index]];

      return newSorted.map((p, i) => ({ ...p, sort_order: i }));
    });
    showSuccess("Order updated.");
  };

  const showForm = isAdding || editing;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1B2838]">Photo Slider</h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage the photo slider displayed on the homepage.
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#4ECDC4] text-white rounded-lg font-semibold text-sm cursor-pointer hover:bg-[#3dbdb5] transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Add Photo
        </button>
      </div>

      {successMsg && (
        <div className="mb-6 px-4 py-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg text-sm">
          {successMsg}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8">
          <h2 className="text-lg font-bold text-[#1B2838] mb-5">
            {isAdding ? "Add New Photo" : "Edit Photo"}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-[#1B2838] mb-1.5">
                Image URL *
              </label>
              <input
                type="text"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://..."
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#4ECDC4] focus:ring-1 focus:ring-[#4ECDC4] transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1B2838] mb-1.5">
                Alt Text
              </label>
              <input
                type="text"
                value={form.alt_text}
                onChange={(e) => setForm({ ...form, alt_text: e.target.value })}
                placeholder="Describe the image..."
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#4ECDC4] focus:ring-1 focus:ring-[#4ECDC4] transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1B2838] mb-1.5">
                Caption (English) *
              </label>
              <input
                type="text"
                value={form.caption_en}
                onChange={(e) => setForm({ ...form, caption_en: e.target.value })}
                placeholder="Caption in English..."
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#4ECDC4] focus:ring-1 focus:ring-[#4ECDC4] transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1B2838] mb-1.5">
                Caption (Pashto)
              </label>
              <input
                type="text"
                value={form.caption_ps}
                onChange={(e) => setForm({ ...form, caption_ps: e.target.value })}
                placeholder="Caption in Pashto..."
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#4ECDC4] focus:ring-1 focus:ring-[#4ECDC4] transition-colors text-right"
                dir="rtl"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1B2838] mb-1.5">
                Caption (Dari)
              </label>
              <input
                type="text"
                value={form.caption_da}
                onChange={(e) => setForm({ ...form, caption_da: e.target.value })}
                placeholder="Caption in Dari..."
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#4ECDC4] focus:ring-1 focus:ring-[#4ECDC4] transition-colors text-right"
                dir="rtl"
              />
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-[#4ECDC4] focus:ring-[#4ECDC4]"
                />
                <span className="text-sm text-[#1B2838] font-medium">Visible on site</span>
              </label>
            </div>
          </div>

          {/* Image preview */}
          {form.image_url && (
            <div className="mt-5">
              <p className="text-xs text-gray-400 mb-2">Preview:</p>
              <div className="relative w-full aspect-[16/7] rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={form.image_url}
                  alt={form.alt_text || "Preview"}
                  className="w-full h-full object-cover object-top"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mt-6">
            <button
              onClick={handleSave}
              className="px-6 py-2.5 bg-[#4ECDC4] text-white rounded-lg font-semibold text-sm cursor-pointer hover:bg-[#3dbdb5] transition-colors whitespace-nowrap"
            >
              {isAdding ? "Add Photo" : "Save Changes"}
            </button>
            <button
              onClick={handleCancel}
              className="px-6 py-2.5 bg-gray-100 text-gray-600 rounded-lg font-semibold text-sm cursor-pointer hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Photo list */}
      <div className="space-y-4">
        {photos.map((photo, index) => (
          <div
            key={photo.id}
            className={`bg-white border rounded-xl overflow-hidden transition-all ${
              photo.is_active ? "border-gray-200" : "border-gray-100 opacity-60"
            }`}
          >
            <div className="flex flex-col md:flex-row">
              {/* Thumbnail */}
              <div className="w-full md:w-64 h-40 md:h-auto flex-shrink-0 bg-gray-100">
                <img
                  src={photo.image_url}
                  alt={photo.alt_text}
                  className="w-full h-full object-cover object-top"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>

              {/* Info */}
              <div className="flex-1 p-5 flex flex-col justify-between">
                <div>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <p className="text-sm text-gray-500">
                      #{index + 1} &middot; Order: {photo.sort_order}
                    </p>
                    {!photo.is_active && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                        Hidden
                      </span>
                    )}
                  </div>
                  <p className="text-[#1B2838] font-semibold text-sm mb-1 line-clamp-1">
                    {photo.caption_en}
                  </p>
                  <p className="text-xs text-gray-400 line-clamp-1">{photo.image_url}</p>
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-4">
                  <button
                    onClick={() => movePhoto(photo.id, "up")}
                    disabled={index === 0}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move up"
                  >
                    <i className="ri-arrow-up-s-line"></i>
                  </button>
                  <button
                    onClick={() => movePhoto(photo.id, "down")}
                    disabled={index === photos.length - 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move down"
                  >
                    <i className="ri-arrow-down-s-line"></i>
                  </button>

                  <button
                    onClick={() => handleToggleActive(photo.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                      photo.is_active
                        ? "bg-amber-50 text-amber-700 hover:bg-amber-100"
                        : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                    }`}
                  >
                    {photo.is_active ? "Hide" : "Show"}
                  </button>

                  <div className="flex-1"></div>

                  <button
                    onClick={() => handleEdit(photo)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-edit-line mr-1"></i>
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(photo.id)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-50 text-rose-600 hover:bg-rose-100 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {photos.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
              <i className="ri-image-line text-2xl"></i>
            </div>
            <p>No photos yet. Click "Add Photo" to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
}